/**
 * Spaces Page
 *
 * Browse available spaces and their rooms
 */

import { Link } from 'react-router-dom'
import { useState } from 'react'
import { Folder, ChevronRight, Lock, Globe, Users, Plus, Search } from 'lucide-react'
import { trpc } from '@/trpc/client'
import { Visibility } from '@polygraf/shared'
import { useSession } from '@/lib/auth-client'
import { useTranslation } from 'react-i18next'
import { usePageTitle } from '@/hooks/use-page-title'
import {
	Alert,
	Button,
	EmptyState,
	Modal,
	Select,
	Spinner,
	TextField,
	Textarea,
} from '@/components/ui'

/**
 * Split a textarea value (one entry per line) into a trimmed, de-blanked list.
 * Shared by the IP-restriction fields in the create + edit space modals so an
 * IP_RESTRICTED space is never saved with stray-whitespace CIDRs/domains.
 */
export function parseLineList(value: string): string[] {
	return value
		.split('\n')
		.map((line) => line.trim())
		.filter((line) => line.length > 0)
}

export default function SpacesPage() {
	const { t } = useTranslation()
	usePageTitle(t('spaces.title'))
	const { data: spaces, isLoading, error } = trpc.spaces.list.useQuery()
	const { data: session } = useSession()
	const utils = trpc.useUtils()
	const [showCreate, setShowCreate] = useState(false)
	const [search, setSearch] = useState('')

	if (isLoading) {
		return (
			<div className="flex justify-center py-12 text-primary">
				<Spinner size="lg" />
			</div>
		)
	}

	if (error) {
		return (
			<Alert tone="error">
				{t('spaces.loadError')}: {error.message}
			</Alert>
		)
	}

	// Czech collation, case-insensitive (MU #7): the server's Prisma orderBy
	// follows the DB collation, which sorts all uppercase names before any
	// lowercase one ("Testovací" < "místnost").
	const filteredSpaces = (
		spaces?.filter((space) => {
			if (!search) return true
			const q = search.toLowerCase()
			return (
				space.name.toLowerCase().includes(q) ||
				(space.description?.toLowerCase().includes(q) ?? false)
			)
		}) ?? []
	).sort((a, b) => a.name.localeCompare(b.name, 'cs', { sensitivity: 'base' }))

	return (
		<div className="space-y-6 animate-enter">
			<div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
				<div>
					<h1>{t('spaces.title')}</h1>
					<p className="text-base-content/60 mt-1">{t('spaces.subtitle')}</p>
				</div>
				{session?.user && (
					<Button onClick={() => setShowCreate(true)} leadingIcon={<Plus size={18} />}>
						{t('spaces.createSpace')}
					</Button>
				)}
			</div>

			{spaces?.length === 0 ? (
				<div className="card-refined">
					<EmptyState
						icon={<Folder size={28} />}
						title={t('spaces.noSpaces')}
						description={session?.user ? t('spaces.noSpacesAuth') : t('spaces.noSpacesGuest')}
					/>
				</div>
			) : (
				<>
					<TextField
						type="search"
						aria-label={t('spaces.searchPlaceholder')}
						placeholder={t('spaces.searchPlaceholder')}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						leadingIcon={<Search size={18} aria-hidden />}
					/>

					{filteredSpaces.length === 0 ? (
						<div className="card-refined">
							<EmptyState
								icon={<Search size={28} />}
								title={t('spaces.noSpacesMatch')}
								description={t('spaces.noSpacesMatchHint')}
							/>
						</div>
					) : (
						<div className="space-y-3">
							{filteredSpaces.map((space, index) => (
								<SpaceCard key={space.id} space={space} index={index} t={t} />
							))}
						</div>
					)}
				</>
			)}

			{showCreate && (
				<CreateSpaceModal
					open={showCreate}
					onClose={() => setShowCreate(false)}
					onSuccess={() => {
						setShowCreate(false)
						utils.spaces.list.invalidate()
					}}
					t={t}
				/>
			)}
		</div>
	)
}

function SpaceCard({
	space,
	index,
	t,
}: {
	space: {
		id: string
		name: string
		description?: string | null
		visibility: Visibility
		roomCount?: number
		isOwner?: boolean
		canManage?: boolean
	}
	index: number
	t: (key: string, options?: Record<string, unknown>) => string
}) {
	return (
		<Link
			to={`/spaces/${space.id}`}
			className={`card-refined p-4 flex items-center gap-4 animate-enter stagger-${Math.min(index + 1, 5)}`}
		>
			<div className="w-12 h-12 rounded-xl bg-gradient-to-br from-base-200 to-base-300 flex items-center justify-center flex-shrink-0">
				<Folder size={24} className="text-primary" />
			</div>

			<div className="flex-1 min-w-0">
				{/* MU 17. 9.: the name used to `truncate` next to a `flex-shrink-0`
				    visibility badge ("Omezeno na IP/domény uživatelů", ~200px), so on
				    a phone the badge kept its full width and the name collapsed —
				    measured at 0px wide, with the row still overflowing the card by
				    86/46/16px at 320/360/390. Below `sm` the name now wraps and the
				    badge moves to its own line under the text column (0px overflow,
				    name 154/194/224px); from `sm` up the tile is the original single
				    truncating row. */}
				<div className="flex flex-wrap items-center gap-x-2 gap-y-1 sm:flex-nowrap">
					<p className="font-semibold text-lg min-w-0 break-words sm:truncate">{space.name}</p>
					{space.isOwner && (
						<span className="px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary">
							{t('spaces.owner')}
						</span>
					)}
				</div>
				{/* `line-clamp-2` and `truncate` set conflicting `display`/`white-space`,
				    so scope the clamp to below `sm` instead of stacking them. */}
				{space.description && (
					<p className="text-sm text-base-content/60 break-words max-sm:line-clamp-2 sm:truncate">
						{space.description}
					</p>
				)}
				{space.roomCount !== undefined && (
					<p className="text-xs text-base-content/40 mt-1">
						{t('spaces.roomCount', { count: space.roomCount })}
					</p>
				)}
				<div className="flex mt-2 sm:hidden">
					<VisibilityBadge visibility={space.visibility} t={t} />
				</div>
			</div>

			<div className="flex items-center gap-3 flex-shrink-0">
				<div className="hidden sm:flex">
					<VisibilityBadge visibility={space.visibility} t={t} />
				</div>
				<ChevronRight size={20} className="text-base-content/40" />
			</div>
		</Link>
	)
}

function VisibilityBadge({
	visibility,
	t,
}: {
	visibility: Visibility
	t: (key: string) => string
}) {
	const styles = {
		[Visibility.PUBLIC]: {
			bg: 'bg-sage-500/10',
			// F-010: sage[500] (#5d8a6a) on sage-tinted bg = 3.53:1, fails WCAG 1.4.3.
			// sage[600] (#446b51) measures ~6:1 on the same bg — passes AA.
			text: 'text-sage-600',
			icon: Globe,
			labelKey: 'spaces.visibility.public',
		},
		[Visibility.AUTHENTICATED]: {
			bg: 'bg-primary/10',
			text: 'text-primary',
			icon: Users,
			labelKey: 'spaces.visibility.authenticated',
		},
		[Visibility.PRIVATE]: {
			bg: 'bg-coral-500/10',
			text: 'text-coral-700',
			icon: Lock,
			labelKey: 'spaces.visibility.private',
		},
		[Visibility.IP_RESTRICTED]: {
			bg: 'bg-base-300',
			text: 'text-base-content/70',
			icon: Globe,
			labelKey: 'spaces.visibility.ipRestricted',
		},
	}

	const style = styles[visibility]
	if (!style) return null

	const Icon = style.icon

	return (
		<div
			className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 ${style.bg} ${style.text}`}
		>
			<Icon size={12} />
			{t(style.labelKey)}
		</div>
	)
}

function CreateSpaceModal({
	open,
	onClose,
	onSuccess,
	t,
}: {
	open: boolean
	onClose: () => void
	onSuccess: () => void
	t: (key: string) => string
}) {
	const [name, setName] = useState('')
	const [description, setDescription] = useState('')
	const [visibility, setVisibility] = useState<Visibility>(Visibility.PRIVATE)
	const [allowedIpRanges, setAllowedIpRanges] = useState('')
	const [allowedDomains, setAllowedDomains] = useState('')
	const [submitting, setSubmitting] = useState(false)

	const createSpaceMutation = trpc.spaces.create.useMutation({
		onSuccess,
	})

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		setSubmitting(true)
		try {
			const isIpRestricted = visibility === Visibility.IP_RESTRICTED
			await createSpaceMutation.mutateAsync({
				name,
				description: description || undefined,
				visibility,
				// Only send the ACL lists for IP_RESTRICTED — they're meaningless for
				// the other tiers and the server defaults them to [].
				allowedIpRanges: isIpRestricted ? parseLineList(allowedIpRanges) : undefined,
				allowedDomains: isIpRestricted ? parseLineList(allowedDomains) : undefined,
			})
		} finally {
			setSubmitting(false)
		}
	}

	return (
		<Modal
			open={open}
			onClose={onClose}
			title={t('spaces.createModal.title')}
			size="md"
			footer={
				<>
					<Button type="button" variant="ghost" onClick={onClose}>
						{t('common.cancel')}
					</Button>
					<Button type="submit" form="create-space-form" loading={submitting} disabled={!name}>
						{t('common.create')}
					</Button>
				</>
			}
		>
			<form id="create-space-form" onSubmit={handleSubmit} className="space-y-4">
				<TextField
					type="text"
					label={`${t('spaces.createModal.nameLabel')} *`}
					value={name}
					onChange={(e) => setName(e.target.value)}
					placeholder={t('spaces.createModal.namePlaceholder')}
					required
					autoFocus
				/>
				<Textarea
					label={t('spaces.createModal.descriptionLabel')}
					value={description}
					onChange={(e) => setDescription(e.target.value)}
					placeholder={t('spaces.createModal.descriptionPlaceholder')}
				/>
				<Select
					label={t('spaces.createModal.visibilityLabel')}
					value={visibility}
					onChange={(e) => setVisibility(e.target.value as Visibility)}
				>
					<option value={Visibility.PUBLIC}>
						{t('spaces.createModal.visibilityOptions.public')}
					</option>
					<option value={Visibility.AUTHENTICATED}>
						{t('spaces.createModal.visibilityOptions.authenticated')}
					</option>
					<option value={Visibility.PRIVATE}>
						{t('spaces.createModal.visibilityOptions.private')}
					</option>
					<option value={Visibility.IP_RESTRICTED}>
						{t('spaces.createModal.visibilityOptions.ipRestricted')}
					</option>
				</Select>

				{visibility === Visibility.IP_RESTRICTED && (
					<>
						<Textarea
							label={t('spaces.createModal.allowedIpRangesLabel')}
							value={allowedIpRanges}
							onChange={(e) => setAllowedIpRanges(e.target.value)}
							placeholder={t('spaces.createModal.allowedIpRangesPlaceholder')}
							rows={3}
						/>
						<Textarea
							label={t('spaces.createModal.allowedDomainsLabel')}
							value={allowedDomains}
							onChange={(e) => setAllowedDomains(e.target.value)}
							placeholder={t('spaces.createModal.allowedDomainsPlaceholder')}
							rows={2}
						/>
						<p className="text-xs text-base-content/60">
							{t('spaces.createModal.restrictionsHint')}
						</p>
					</>
				)}

				{createSpaceMutation.error && (
					<Alert tone="error">{createSpaceMutation.error.message}</Alert>
				)}
			</form>
		</Modal>
	)
}
